/**
 * Opens the STOQ (RestockRocket) notify-me modal from product cards
 * and predictive search.
 *
 * STOQ attaches `window._RestockRocket` only after `stoq:loaded`.
 * That event (and the API) is limited to product, collection, home, and
 * search pages, and only when the app embed is running — not in the
 * theme editor's inline preview pane.
 */
const STOQ_READY_TIMEOUT = 15000;

/** @type {{ api: { openModal: Function }, enabled?: boolean, pageType?: string } | null} */
let stoqReady = null;

function getStoqApi() {
  return window._RestockRocket || window.RestockRocket || null;
}

function getReadyApi() {
  const api = getStoqApi();
  if (api && typeof api.openModal === 'function') return api;
  return null;
}

/**
 * @param {CustomEvent | Event} [event]
 */
function captureStoqReady(event) {
  const api = getReadyApi();
  if (!api) return false;

  const detail = event instanceof CustomEvent ? event.detail : undefined;
  stoqReady = {
    api,
    enabled: detail?.enabled,
    pageType: detail?.pageType,
  };
  return true;
}

window.addEventListener('stoq:loaded', captureStoqReady);
captureStoqReady();

function waitForStoq() {
  if (captureStoqReady()) return Promise.resolve(stoqReady);

  return new Promise((resolve, reject) => {
    const onLoaded = (event) => {
      if (!captureStoqReady(event)) return;
      cleanup();
      resolve(stoqReady);
    };

    const timeout = setTimeout(() => {
      cleanup();
      if (captureStoqReady()) {
        resolve(stoqReady);
        return;
      }

      reject(
        new Error(
          'STOQ did not load. Enable the STOQ app embed, turn on Notify me for Collection / Home / Search in the STOQ app, and test in a storefront preview (not the theme editor pane).'
        )
      );
    }, STOQ_READY_TIMEOUT);

    function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener('stoq:loaded', onLoaded);
    }

    window.addEventListener('stoq:loaded', onLoaded);
  });
}

/**
 * @param {HTMLElement} button
 * @returns {object}
 */
function buildProductData(button) {
  const raw = button.dataset.product;
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('STOQ product payload could not be parsed', error);
    }
  }

  const productId = Number(button.dataset.productId);
  const variantId = Number(button.dataset.variantId);

  return {
    id: productId,
    variants: [{ id: variantId, available: false }],
  };
}

/**
 * Shopify IDs in the product JSON are numbers. `dataset` values are always
 * strings, and STOQ looks variants up against that JSON, so the modal id
 * must use the same type.
 *
 * @param {string | number | undefined} value
 * @returns {number | null}
 */
function toShopifyId(value) {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * @param {HTMLElement} button
 * @returns {{ email?: string } | undefined}
 */
function buildCustomerData(button) {
  const email = button.dataset.customerEmail;
  if (!email) return undefined;
  return { email };
}

/**
 * Mobile predictive search is a native <dialog> in the top layer.
 * STOQ's overlay cannot stack above that, so the search modal has to
 * close before openModal or the form stays hidden.
 *
 * @returns {Promise<void> | void}
 */
function closeSearchModal() {
  const searchModal = document.querySelector('#search-modal');
  if (searchModal && typeof searchModal.closeDialog === 'function') {
    return searchModal.closeDialog();
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-stoq-notify]');
  if (!(button instanceof HTMLElement)) return;

  event.preventDefault();
  event.stopPropagation();

  const variantId = toShopifyId(button.dataset.variantId);
  if (!variantId) return;

  button.setAttribute('disabled', '');

  waitForStoq()
    .then(async (ready) => {
      await closeSearchModal();
      ready.api.openModal(buildProductData(button), variantId, buildCustomerData(button));
    })
    .catch((error) => {
      console.warn(error?.message || 'STOQ notify modal could not be opened', error);
    })
    .finally(() => {
      button.removeAttribute('disabled');
    });
});
